const COMPLETE_STATUS = 'Complete';

const sameId = (a, b) => String(a ?? null) === String(b ?? null);

const samePeriod = (flagPeriod, period) => {
   if (!flagPeriod || !period) {
      return false;
   }

   return (
      String(flagPeriod.lower) === String(period.lower) &&
      String(flagPeriod.upper) === String(period.upper)
   );
};

export const getRelevantFlags = ({
   flags = [],
   period = null,
   studyId = null,
   checkSection = null,
   userId = null,
   includeAllUsers = false,
} = {}) =>
   flags.filter((flag) => {
      const matchesPeriod = period ? samePeriod(flag.period, period) : true;
      const matchesStudy = studyId != null ? sameId(flag.studyId, studyId) : true;
      const matchesSection = checkSection ? flag.ruleName === checkSection : true;
      const matchesUser = includeAllUsers ? true : sameId(flag.userId, userId);

      return matchesPeriod && matchesStudy && matchesSection && matchesUser;
   });

export const isUnresolvedFlag = (flag, FlagStates) =>
   flag.severityLevel === FlagStates.MUST_FIX ||
   (flag.severityLevel === FlagStates.MUST_VERIFY && !flag.verified);

export const hasUnresolvedFlags = (flags, FlagStates) =>
   flags.some((flag) => isUnresolvedFlag(flag, FlagStates));

export const areAllFlagsResolved = (flags, FlagStates) =>
   flags.every((flag) => !isUnresolvedFlag(flag, FlagStates));

export const isAssignmentMarkCompletedDisabled = ({
   isCompleted,
   hasPendingSaveRequests,
   isRndig,
   statuses,
   selfStatus,
   hasCompletedAssignmentsInPeriod,
   hasUnresolvedFlags,
   flagsStale,
}) => {
   if (isCompleted || hasPendingSaveRequests) {
      return true;
   }

   const areStatusesIncomplete = isRndig
      ? statuses.some((status) => status !== COMPLETE_STATUS)
      : selfStatus !== COMPLETE_STATUS;

   if (areStatusesIncomplete) {
      return true;
   }

   if (hasCompletedAssignmentsInPeriod) {
      return true;
   }

   // A flag raised before this period's data was last edited would otherwise
   // permanently block completion -- the server only re-runs data-check rules on a
   // completion attempt (see CompanySetPropsMixin._put), so nothing else would ever
   // clear a stale flag. `flagsStale` (set on every summary save, cleared once flags
   // are reloaded) gives the user another attempt after an edit, or after explicitly
   // verifying the flag(s), without waiting on a fresh server-side re-validation that
   // this same disabled button would otherwise prevent from ever being triggered.
   return hasUnresolvedFlags && !flagsStale;
};

export const isSectionMarkCompletedDisabled = ({
   crossPeriodMergedStatuses,
   isCompleted,
   hasUnresolvedFlags,
   hasPendingSaveRequests,
   allAssignmentsCompleted,
}) => {
   const statuses = Object.values(crossPeriodMergedStatuses);

   // Unlike isAssignmentMarkCompletedDisabled, flags ARE checked here: "Mark Section
   // Complete" submits with no periodId (see ExpensesCard.vue's submit(false)), and
   // CompanySetPropsMixin._put only re-runs the data-check rules when a periodId is
   // present -- this action has no server-side re-validation safety net at all, so
   // this client-side check is the only thing standing between it and a genuinely
   // unresolved flag. By the time every assignee's own "Mark Assignment Complete" has
   // gone through (a prerequisite below, via allAssignmentsCompleted), each of them has
   // already forced their own re-validation + flags reload, so this isn't stale in the
   // same way the per-assignment check was.
   if (statuses.length === 0 || isCompleted || hasUnresolvedFlags || hasPendingSaveRequests) {
      return true;
   }

   // Previously this only checked the unassigned (null) bucket's status,
   // not every assignee's -- allAssignmentsCompleted (everyone has clicked
   // "Mark Assignment Complete") doesn't guarantee the section's overall
   // status still reads Complete (data can change after marking, or a bug
   // upstream can leave the two out of sync -- see the auto-fill save-race
   // fix), so require every merged status, not just the unassigned one.
   const allStatusesComplete = statuses.every((status) => status === COMPLETE_STATUS);
   const canMarkCompleted = allAssignmentsCompleted && allStatusesComplete;

   return !canMarkCompleted;
};
